import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { ShieldCheckIcon } from './icons'
import { SocialLinks } from './SocialLinks'
import { LegalLinks } from './LegalLinks'
import { LogoMark } from './LogoMark'
import { getLandingPagesByTopic } from '../../content/landingPages'
import { blogListPath } from '../../content/blog'
import { resolveSupportedLang, type SupportedLanguage } from '../../i18n'

// Sitewide interlinking for the SEO landing pages (see content/landingPages) - the footer is the
// one place present on every marketing surface (landing page, blog, /pv-rechner, and the landing
// pages themselves), so it gives these pages their widest and most consistent internal-link
// coverage rather than relying only on the topical cross-links inside each page's own content.
// Localized per current UI language - short nav labels, distinct from the pages' own full titles.
const GUIDE_TOPICS: { topicId: string; label: Record<SupportedLanguage, string> }[] = [
  {
    topicId: 'solaranlage-kosten',
    label: { de: 'Solaranlage Kosten', es: 'Coste de la instalación', pl: 'Koszt instalacji' },
  },
  {
    topicId: 'pv-angebot-vergleichen',
    label: { de: 'Angebote vergleichen', es: 'Comparar presupuestos', pl: 'Porównaj oferty' },
  },
  {
    topicId: 'photovoltaik-anfrage-erstellen',
    label: { de: 'Anfrage erstellen', es: 'Solicitar presupuesto', pl: 'Złóż zapytanie' },
  },
  {
    topicId: 'pv-installateur-finden',
    label: { de: 'Installateur finden', es: 'Encontrar instalador', pl: 'Znajdź instalatora' },
  },
  {
    topicId: 'photovoltaik-planen',
    label: { de: 'Photovoltaik planen', es: 'Planificar instalación', pl: 'Zaplanuj instalację' },
  },
  {
    topicId: 'pv-module-montage',
    label: { de: 'Nur Dachmontage', es: 'Solo montaje en tejado', pl: 'Tylko montaż na dachu' },
  },
  {
    topicId: 'photovoltaik-elektroinstallation',
    label: { de: 'Nur Elektroinstallation', es: 'Solo instalación eléctrica', pl: 'Tylko instalacja elektryczna' },
  },
  {
    topicId: 'photovoltaik-ohne-telefonnummer',
    label: { de: 'Ohne Telefonnummer', es: 'Sin número de teléfono', pl: 'Bez numeru telefonu' },
  },
  {
    topicId: 'photovoltaik-waermepumpe',
    label: { de: 'Mit Wärmepumpe', es: 'Con bomba de calor', pl: 'Z pompą ciepła' },
  },
  {
    topicId: 'photovoltaik-wallbox-elektroauto',
    label: { de: 'Mit Wallbox/E-Auto', es: 'Con wallbox/coche eléctrico', pl: 'Z wallboxem/autem elektrycznym' },
  },
  {
    topicId: 'photovoltaik-foerderung',
    label: { de: 'Förderung', es: 'Ayudas', pl: 'Dofinansowanie' },
  },
  {
    topicId: 'handwerker-photovoltaik-auftraege',
    label: { de: 'Für Handwerksbetriebe', es: 'Para instaladores', pl: 'Dla instalatorów' },
  },
  // GitLab-Issue #143: zusätzliche Auffindbarkeit über den Footer, wie in #142 gefordert - die
  // prominente Platzierung bleibt der Hero-Link (LandingPage.tsx), das hier ersetzt ihn nicht.
  {
    topicId: 'so-funktioniert-es-fuer-kunden',
    label: { de: 'Anleitung für Kunden', es: 'Guía para clientes', pl: 'Instrukcja dla klientów' },
  },
  {
    topicId: 'so-funktioniert-es-fuer-handwerker',
    label: { de: 'Anleitung für Handwerker', es: 'Guía para instaladores', pl: 'Instrukcja dla instalatorów' },
  },
]

/** The dark footer shared by the landing page and marketing content (blog) - see MarketingHeader
 *  for why this is a separate component instead of living inside LandingPage. */
export function MarketingFooter() {
  const { t, i18n } = useTranslation()
  const lang = resolveSupportedLang(i18n.resolvedLanguage)
  const guideLinks = GUIDE_TOPICS.flatMap(({ topicId, label }) => {
    const page = getLandingPagesByTopic(topicId).find((p) => p.lang === lang)
    return page ? [{ href: `/${page.slug}`, label: label[lang] }] : []
  })

  return (
    <footer className="landing-footer">
      <nav className="landing-footer-guides" aria-label="Ratgeber">
        {guideLinks.map((link) => (
          <Link key={link.href} to={link.href}>
            {link.label}
          </Link>
        ))}
      </nav>
      <div className="landing-footer-inner">
        <span>
          <LogoMark className="landing-logo-icon" /> Roofora
          <span className="beta-badge">Beta</span>
        </span>
        <span className="muted">
          <ShieldCheckIcon /> {t('landing.footer.privacyNote')}
        </span>
        <Link to={blogListPath(lang)} className="landing-footer-blog-link">
          Blog
        </Link>
        <Link to="/partner" className="landing-footer-blog-link">
          Partner
        </Link>
        <LegalLinks />
        <SocialLinks className="landing-footer-social-links" linkClassName="landing-footer-social-link" />
        <span className="muted">© {new Date().getFullYear()} Roofora</span>
      </div>
    </footer>
  )
}
